import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./GuestDetailsModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar, faTrophy, faBed, faComment, faXmark, faPen, faTrash, faPhone,
} from "@fortawesome/free-solid-svg-icons";
import type { Guest, Booking } from "./types";

interface Props {
  open: boolean;
  guest: Guest | null;
  bookings: Booking[];
  onClose: () => void;
  onEdit: (guest: Guest) => void;
  onDelete: (guestId: number) => void;
  loading?: boolean;
}

function getInitials(name: string) {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

function avatarColor(name: string) {
  const colors = ["#6366f1","#8b5cf6","#ec4899","#f59e0b","#10b981","#3b82f6","#ef4444","#14b8a6"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

const statusColor: Record<string, string> = {
  confirmed: "#10b981",
  cancelled: "#ef4444",
  "checked-in": "#6366f1",
  "checked-out": "#64748b",
};

export default function GuestDetailsModal({
  open,
  guest,
  bookings,
  onClose,
  onEdit,
  onDelete,
  loading = false,
}: Props) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();

  if (!open || !guest) return null;

  const name = guest.name || "Unknown";
  const color = avatarColor(name);
  const totalSpent = bookings.reduce((s, b) => s + (Number(b.Total) || 0), 0);

  const fmt = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const handleClose = () => {
    setConfirmDelete(false);
    onClose();
  };

  const handleDeleteConfirm = () => {
    if (guest.id) {
      onDelete(guest.id);
    }
  };

  return (
    <div className="gm-overlay" onClick={handleClose}>
      <div className="gm-modal" onClick={e => e.stopPropagation()}>

        {/* CLOSE */}
        <button className="gm-close" onClick={handleClose}>
          <FontAwesomeIcon icon={faXmark} />
        </button>

        {/* HEADER */}
        <div className="gm-header">
          <div className="gm-avatar" style={{ background: color }}>
            {getInitials(name)}
          </div>
          <div className="gm-header-info">
            <h2 className="gm-name">{name}</h2>
            <div className="gm-tags">
              {guest.isVIP && (
                <span className="gm-tag vip">
                  <FontAwesomeIcon icon={faStar} style={{ marginRight: 4 }} />
                  VIP
                </span>
              )}
              {guest.RoomNumber && (
                <span className="gm-tag room">
                  <FontAwesomeIcon icon={faBed} style={{ marginRight: 4 }} />
                  Room {guest.RoomNumber}
                </span>
              )}
              <span className="gm-tag pts">
                <FontAwesomeIcon icon={faTrophy} style={{ marginRight: 4 }} />
                {guest.loyaltyPoints ?? 0} pts
              </span>
            </div>
          </div>
          {totalSpent > 0 && (
            <div className="gm-total-spent">
              <span className="gm-spent-label">Total Spent</span>
              <span className="gm-spent-value">EGP {totalSpent.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* INFO GRID */}
        <div className="gm-info-grid">
          <div className="gm-info-item">
            <span className="gm-info-label">Phone</span>
            <span className="gm-info-value">{guest.phoneNumber || "—"}</span>
          </div>
          <div className="gm-info-item">
            <span className="gm-info-label">Email</span>
            <span className="gm-info-value">{guest.email || "—"}</span>
          </div>
          <div className="gm-info-item">
            <span className="gm-info-label">WhatsApp ID</span>
            <span className="gm-info-value">
              {guest.WaId
                ? <><FontAwesomeIcon icon={faComment} style={{ marginRight: 6, color: "#25D366" }} />{guest.WaId}</>
                : "—"}
            </span>
          </div>
          <div className="gm-info-item">
            <span className="gm-info-label">Birthday</span>
            <span className="gm-info-value">{fmt(guest.Birthday as string)}</span>
          </div>
        </div>

        {/* DELETE CONFIRM BOX */}
        {confirmDelete && (
          <div style={{
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: 12,
            padding: "14px 18px",
            marginBottom: 16,
          }}>
            <p style={{ margin: "0 0 10px", fontSize: 14, color: "#fca5a5" }}>
              Are you sure you want to delete <strong style={{ color: "#ef4444" }}>{name}</strong>?
              This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="gm-btn"
                style={{ background: "#ef4444", color: "#0E1E38", border: "none" }}
                onClick={handleDeleteConfirm}
              >
                Yes, Delete
              </button>
              <button
                className="gm-btn close"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* BOOKINGS */}
        <div className="gm-section-header">
          <span className="gm-section-title">Booking History</span>
          <span className="gm-section-count">{bookings.length}</span>
        </div>

        {loading && (
          <div className="gm-loading">
            <div className="gm-spinner" />
            <span>Loading bookings…</span>
          </div>
        )}

        {!loading && bookings.length === 0 && (
          <div className="gm-no-bookings">No bookings found</div>
        )}

        {!loading && bookings.length > 0 && (
          <div className="gm-bookings-list">
            {bookings.map(b => {
              const sc = statusColor[b.Status?.toLowerCase() ?? ""] ?? "#64748b";
              return (
                <div key={b.BookingID} className="gm-booking-card">
                  <div className="gm-booking-left">
                    <span className="gm-booking-id">{b.BookingID}</span>
                    <span className="gm-booking-room">Room {b.RoomID || "—"}</span>
                  </div>
                  <div className="gm-booking-mid">
                    <span className="gm-booking-dates">
                      {fmt(b.CheckIn as string)} → {fmt(b.CheckOut as string)}
                    </span>
                    <span className="gm-booking-nights">
                      {b.Nights} night{b.Nights !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="gm-booking-right">
                    <span className="gm-booking-total">
                      {b.Total ? `EGP ${Number(b.Total).toLocaleString()}` : "—"}
                    </span>
                    <span className="gm-booking-status" style={{ color: sc, borderColor: sc }}>
                      {b.Status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* FOOTER */}
        <div className="gm-footer">
          {(guest.WaId || guest.phoneNumber) && (
            <button
              className="gm-btn wa"
              onClick={() => {
                const waId = guest.WaId || guest.phoneNumber.replace(/\D/g, "");
                navigate("/dashboard/manager/chat", { state: { openWaId: waId } });
              }}
            >
              <FontAwesomeIcon icon={faComment} /> Live Chat
            </button>
          )}
          {guest.phoneNumber && (
            <a
              className="gm-btn call"
              href={`tel:+${guest.phoneNumber.replace(/\D/g, "")}`}
            >
              <FontAwesomeIcon icon={faPhone} /> Call
            </a>
          )}
          <button
            className="gm-btn edit-btn"
            onClick={() => onEdit(guest)}
          >
            <FontAwesomeIcon icon={faPen} /> Edit
          </button>
          {guest.id && !confirmDelete && (
            <button
              className="gm-btn delete-btn"
              onClick={() => setConfirmDelete(true)}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete
            </button>
          )}
          <button className="gm-btn close" onClick={handleClose}>
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
